import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { TranslocoModule } from '@jsverse/transloco';
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { QaiTableComponent, TableRow, HeaderConfig, QaiIconComponent, QaiSearchableSelectComponent, QaiSearchableSelectOption, QaiDatepickerComponent, SortDirection } from '@qai/ui';
import { DetailsSectionComponent } from 'src/app/pages/self-serve/simulation-builder/components/details-container/details-section.component';
import { IUserSessionResponse } from 'src/app/interfaces/reportingV2/iuser-session-stats-response';
import { CommonModule } from '@angular/common';
import { CsvExportService, csvValue } from 'src/app/services/general/csv-export.service';
import { LocaleService } from 'src/app/services/locale.service';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { ReportingLocalStorageUtil } from 'src/app/utils/reporting-localstorage.util';
import { IUserSessionRequest } from 'src/app/interfaces/reportingV2/iuser-session-stats-request';
import { AppConstants } from 'src/app/app-constants';
import { ActivatedRoute, Router } from '@angular/router';
import Highcharts from 'highcharts';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { BreakpointObserver } from '@angular/cdk/layout';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-user-sessions',
  templateUrl: './user-sessions.component.html',
  styleUrls: ['./user-sessions.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslocoModule, QaiTableComponent, DetailsSectionComponent, QaiIconComponent, QaiSearchableSelectComponent, QaiDatepickerComponent],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    }
  ],
})
export class UserSessionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() totalSessionsCount: number = 0;
  @Input() userName: string = '';
  @Input() userUuid: string = '';
  @Input() currentTab: string = '';
  readonly datePickerFormatter = (date: Date): string => this.localeService.formatDate(date);
  startDate: string | null = null;
  endDate: string | null = null;
  minStartDate: string = '';
  maxStartDate: string = '';
  minEndDate: string = '';
  maxEndDate: string = '';
  accountUuid: string | null = null;
  isSmallScreen: boolean = false;

  isTranslationLoaded = false;
  isLoading = false;
  currentPage = 1;
  initialSortColumn: string = '';
  initialSortDirection: SortDirection = 'desc';
  currentSortDirection: 'asc' | 'desc' | null = 'desc';
  private userSessionsSub?: Subscription;

  headers: HeaderConfig[] = [];
  tableData: TableRow[] = [];
  originalData: TableRow[] = [];
  paginatedData: TableRow[] = [];

  // Filter options
  assignmentOptions: QaiSearchableSelectOption[] = [];
  simulationOptions: QaiSearchableSelectOption[] = [];

  // Selected filter values
  selectedAssignments: string[] | string = [];
  selectedSimulations: string[] | string = [];
  assignmentClearOption = { enable: true, label: 'Clear' };
  simulationClearOption = { enable: true, label: 'Clear' };

  othersTextFormatter = (count: number): string =>
    count === 1
      ? this.translocoService.translate('OTHERS_SINGULAR', {}, 'reportingV2')
      : this.translocoService.translate('OTHERS_PLURAL', { count }, 'reportingV2');

  assignmentSimulationMap: Record<string, string[]> = {};
  simulationAssignmentMap: Record<string, string[]> = {};

  userSessions: IUserSessionResponse[] = [];

  scopedOptionsSelectionModeAssignments = {
    enable: true,
    selectAllOption: { value: 'selectAll', label: 'Select all' },
    deselectAllOption: { value: 'deselectAll', label: 'Deselect all' },
  };
  scopedOptionsSelectionModeSimulations = {
    enable: true,
    selectAllOption: { value: 'selectAll', label: 'Select all' },
    deselectAllOption: { value: 'deselectAll', label: 'Deselect all' },
  };

  @ViewChild(QaiTableComponent) tableComponent?: QaiTableComponent;
  @ViewChild('scatterPlotContainer', { static: false })
  scatterPlotContainer!: ElementRef;

  chart: Highcharts.Chart | undefined | null;
  chartOptions: Highcharts.Options = {};
  private idToStatsMap = new Map<string | number, IUserSessionResponse>();
  private scatterPlotInitialized = false;
  private isSingleSimulationPracticeAssignment = false;
  private isTableSinglePracticeAssignmentAndSimulation = false;

  constructor(
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    private csvExportService: CsvExportService,
    private localeService: LocaleService,
    private reportingDashboardService: ReportingDashboardService,
    private route: ActivatedRoute,
    private router: Router,
    private amplitudeService: AmplitudeService,
    private breakpointObserver: BreakpointObserver,
  ) { }

  ngOnInit(): void {
    this.translationService.loadScopedTranslation('reportingV2')
      .then(() => {
        this.isTranslationLoaded = true;
        this.scopedOptionsSelectionModeAssignments.selectAllOption!.label =
          this.translocoService.translate('SELECT_ALL', {}, 'reportingV2');
        this.scopedOptionsSelectionModeAssignments.deselectAllOption!.label =
          this.translocoService.translate('DESELECT_ALL', {}, 'reportingV2');
        this.scopedOptionsSelectionModeSimulations.selectAllOption!.label =
          this.translocoService.translate('SELECT_ALL', {}, 'reportingV2');
        this.scopedOptionsSelectionModeSimulations.deselectAllOption!.label =
          this.translocoService.translate('DESELECT_ALL', {}, 'reportingV2');
        this.assignmentClearOption.label = this.translocoService.translate('CLEAR', {}, 'reportingV2');
        this.simulationClearOption.label = this.translocoService.translate('CLEAR', {}, 'reportingV2');
        this.accountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();
        this.initializeTable();
        this.breakpointObserver.observe(['(max-width: 768px)'])
        .subscribe(result => {
          this.isSmallScreen = result.matches;
          this.tryInitializeScatterPlot();
        });
        this.loadSessions();
      })
      .catch((error) => {
        this.isTranslationLoaded = false;
      });
  }

  private getAssignmentUuidFromQueryParams(): string | null {
    return this.route.snapshot.queryParams['assignmentUuid'] || null;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['currentTab'] || changes['userUuid']) {
      this.loadSessions();
      this.updateScatterPlot();
    }
  }

  get showScatterPlot(): boolean {
    if (this.currentTab !== 'practice') return false;
    const hasAssignmentandSimulationFilterSelection =
      this.selectedAssignments.length === 1 &&
      this.selectedSimulations.length === 1;
    return hasAssignmentandSimulationFilterSelection || this.isSingleSimulationPracticeAssignment || this.isTableSinglePracticeAssignmentAndSimulation;
  }

  private updateScatterPlot(): void {
    if (this.showScatterPlot) {
      this.scatterPlotInitialized = false;
      setTimeout(() => {
        this.updatePaginatedData();
        this.tryInitializeScatterPlot();
      }, 0);
    } else {
      this.destroyScatterPlot();
    }
  }

  private destroyScatterPlot(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
      this.scatterPlotInitialized = false;
    }
  }

  private tryInitializeScatterPlot(): void {
    if (!this.scatterPlotContainer) return;
    if (!this.showScatterPlot) return;

    if (!this.scatterPlotInitialized) {
      this.initializeScatterPlot();
      this.scatterPlotInitialized = true;
    }
  }

  private initializeScatterPlot(): void {

    const sessions = this.loadChartSessions();

    const { series, xMin, xMax } = this.buildChartData(sessions);

    let xValues: number[] = series?.[0]?.data
      .map(p => p.x)
      .filter((v): v is number => v !== undefined) ?? [];

    this.scatterPlotContainer.nativeElement.innerHTML = '';
    const chartContainerWidth = this.scatterPlotContainer.nativeElement.offsetWidth;
    const isSmallScreen = this.isSmallScreen;
    const hasMultiplePoints = xValues.length > 1;

    if (!series.length || !series[0].data.length) {
      const noDataEl = document.createElement('div');
      noDataEl.innerText = this.translocoService.translate('NO_SESSIONS_AVAILABLE', {}, 'reportingV2');
      noDataEl.style.fontFamily = this.localeService.getFontFamily();
      noDataEl.style.fontWeight = '600';
      noDataEl.style.fontSize = '16px';
      noDataEl.style.color = '#475467';
      noDataEl.style.textAlign = 'center';
      noDataEl.style.padding = '40px 0';
      this.scatterPlotContainer.nativeElement.appendChild(noDataEl);
      return;
    }

    this.chartOptions = {
      chart: {
        type: 'scatter',
        marginRight: 40,
        ...(this.isSmallScreen && xValues.length > 1 && {
          scrollablePlotArea: {
            minWidth: 1500,
            scrollPositionX: 0
          }
        }),
        events: {
          load: function () {
            if (!isSmallScreen || !hasMultiplePoints) return;
            const labels = this.xAxis[0].ticks;
            const firstTick = Object.values(labels)[0] as any;
            if (firstTick && firstTick.label) {
              firstTick.label.attr({ x: firstTick.label.attr('x') + 40 });
            }
          }
        }
      },
      title: {
        text: this.translocoService.translate('SESSION_SCORES_OVER_TIME', {}, 'reportingV2'),
        align: 'left',
        margin: 10,
        style: { fontSize: '16px', fontWeight: '600', fontFamily: this.localeService.getFontFamily() },
      },
      xAxis: {
        type: 'datetime',
        title: {
          text: this.translocoService.translate('TIME', {}, 'reportingV2'),
          margin: 20,
          style: { fontWeight: '500', fontFamily: this.localeService.getFontFamily() },
        },
        scrollbar: {
            enabled: true
        },
        min: xMin,
        max: xMax,
        startOnTick: true,
        endOnTick: true,
        labels: { formatter: function () {
          const monthDay = Highcharts.dateFormat('%B %e', Number(this.value));
          const year = Highcharts.dateFormat('%Y', Number(this.value));
          const time = Highcharts.dateFormat('%I:%M %p', Number(this.value));
          return `<div style="text-align:center; line-height:1.2;">${monthDay}<br>${year}<br>${time}</div>`;
        },
        padding: 10, 
      },
        tickPixelInterval: this.isSmallScreen ? 600 : Math.max(50, chartContainerWidth / 4)
      },
      yAxis: {
        min: 0,
        max: 5,
        tickInterval: 1,
        title: {
          text: this.translocoService.translate('SCORE', {}, 'reportingV2'),
          margin: 40,
          style: { fontWeight: '500', fontFamily: this.localeService.getFontFamily(), whiteSpace: 'nowrap' },
        },
      },
      legend: {
        enabled: false
      },
      tooltip: {
        useHTML: true,
        backgroundColor: '#027B8C',
        borderColor: '#027B8C',
        borderRadius: 8,
        padding: 12,
        style: { color: '#FFFFFF', fontFamily: this.localeService.getFontFamily() },
        formatter: function () {
          const c = (this.point as any).custom;
          return `
            <div>
              <div style="font-weight:600; margin-bottom:6px;">
                ${c.formattedDate}
              </div>
              <div>Session ID: ${c.sessionId}</div>
              <div>Duration: ${c.duration}</div>
              <div>Score: ${this.y}</div>
            </div>
          `;
        },
      },
      credits: { enabled: false },
      series,
    };

    this.drawScatterPlot();
  }

  private loadChartSessions(): IUserSessionResponse[] {

    const visibleSessions = this.getVisibleSessions();

    return visibleSessions;
  }

  private buildChartData(sessions: any[]) {

    if (!sessions?.length) {
      return { series: [], xMin: undefined, xMax: undefined };
    }

    const points: Highcharts.PointOptionsObject[] = sessions.map(s => {
      const dateObj = new Date(s.scoredAt);
      const sessionTimestampUTC = Date.UTC(
        dateObj.getFullYear(),
        dateObj.getMonth(),
        dateObj.getDate(),
        dateObj.getHours(),
        dateObj.getMinutes(),
        dateObj.getSeconds()
      );

      return {
        x: sessionTimestampUTC,
        y: s.scoreOverall,
        custom: {
          sessionId: s.sessionUuid,
          duration: this.localeService.formatDurationInSecondsWithLabel(s.durationInSeconds),
          formattedDate: this.localeService.formatDateLong(s.scoredAt)
        }
      };
    });

    const series = [{
      type: 'scatter' as const,
      data: points,
      color: '#52A4B0',
      marker: { symbol: 'circle' },
    }];

    const timestamps = points.map(point => point.x) as number[];
    const firstSessionDate = Math.min(...timestamps);
    const lastSessionDate = Math.max(...timestamps);
    const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
    const thirtyDaysBeforeLastSession = lastSessionDate - thirtyDaysInMs;
    const xMin = Math.max(firstSessionDate, thirtyDaysBeforeLastSession);
    const xMax = lastSessionDate;

    return { series, xMin, xMax };
  }

  private drawScatterPlot() {
    if (this.scatterPlotContainer) {
      this.chart = Highcharts.chart(this.scatterPlotContainer.nativeElement, this.chartOptions);
    }
  }

  private initializeTable() {
    this.headers = [
      {
        name: this.translocoService.translate('ASSIGNMENT_NAME', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '16%'
      },
      {
        name: this.translocoService.translate('SIMULATION_NAME', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '15%'
      },
      {
        name: this.translocoService.translate('SESSION_DATE', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '12%'
      },
      {
        name: this.translocoService.translate('DURATION_OF_SESSION', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '12%'
      },
      {
        name: this.translocoService.translate('OVERALL_SCORE', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '10%'
      },
      {
        name: this.translocoService.translate('MESSAGING_SCORE_LABEL', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '11%'
      },
      {
        name: this.translocoService.translate('METHODOLOGY_SCORE', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '13%'
      },
      {
        name: this.translocoService.translate('SKILLS_SCORE', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '11%'
      }
    ];
    this.initialSortColumn = this.headers[2].name;
  }

  private getModuleAssignmentType(): string | null {
    if (!this.currentTab) return null;

    switch (this.currentTab) {
      case 'practice':
        return AppConstants.TYPE_PRACTICE;
      case 'certification':
        return AppConstants.TYPE_CERTIFICATION;
      default:
        return null;
    }
  }

  private buildAssignmentSimulationMaps(): void {
    if (!this.originalData?.length) return;

    const assignmentColumnHeader =
      this.translocoService.translate('ASSIGNMENT_NAME', {}, 'reportingV2');
    const simulationColumnHeader =
      this.translocoService.translate('SIMULATION_NAME', {}, 'reportingV2');

    this.assignmentSimulationMap = {};
    this.simulationAssignmentMap = {};

    this.originalData.forEach(row => {
      const assignmentCell = row[assignmentColumnHeader];
      const simulationCell = row[simulationColumnHeader];

      if (assignmentCell?.type === 'text' && simulationCell?.type === 'text') {
        const assignment = assignmentCell.value;
        const simulation = simulationCell.value;

        if (!this.assignmentSimulationMap[assignment]) {
          this.assignmentSimulationMap[assignment] = [];
        }
        if (!this.assignmentSimulationMap[assignment].includes(simulation)) {
          this.assignmentSimulationMap[assignment].push(simulation);
        }

        if (!this.simulationAssignmentMap[simulation]) {
          this.simulationAssignmentMap[simulation] = [];
        }
        if (!this.simulationAssignmentMap[simulation].includes(assignment)) {
          this.simulationAssignmentMap[simulation].push(assignment);
        }
      }
    });
  }

  private loadSessions(): void {
    this.isLoading = true;

    if (this.userSessionsSub) {
      this.userSessionsSub.unsubscribe();
    }

    if (!this.accountUuid || !this.userUuid) {
      console.log('Missing account or user UUID, skipping sessions load');
      this.isLoading = false;
      return;
    }

    const assignmentUuid = this.getAssignmentUuidFromQueryParams();
    const request: IUserSessionRequest = {
      userUuid: this.userUuid,
      accountUuid: this.accountUuid,
      moduleAssignmentUuids: assignmentUuid ? [assignmentUuid] : null,
      type: this.getModuleAssignmentType(),
    };

    this.userSessionsSub = this.reportingDashboardService.getUserSessions(request, withDemoModule(AppConstants.NEW_REPORTING_UI)).subscribe({
      next: (sessions: IUserSessionResponse[]) => {
        this.userSessions = sessions;
        this.originalData = this.createTableRows(sessions);
        this.tableData = [...this.originalData];
        this.clearFilters();
        this.buildAssignmentSimulationMaps();
        this.initializeFilters();
        this.applyFilters();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Failed to load sessions data:', error);
        this.isLoading = false;
      },
    });
  }

  private createTableRows(sessions: IUserSessionResponse[]): TableRow[] {
    return sessions.map(session => {
      const safeValue = (val: any, formatter?: (v: any) => string): string => {
        if (val === null || val === undefined) {
          return '—';
        }
        return formatter ? formatter(val) : val.toString();
      };

      const row: any = {
        id: session.sessionUuid,
        [this.translocoService.translate('ASSIGNMENT_NAME', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(session.moduleAssignmentName)
        },
        [this.translocoService.translate('SIMULATION_NAME', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(session.callName)
        },
        [this.translocoService.translate('SESSION_DATE', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(session.scoredAt, v => this.localeService.formatSessionDateTime(v)),
          ...csvValue(this.localeService.formatCsvDateTime(session.scoredAt)),
          ...(session.scoredAt ? { sortValue: session.scoredAt } : {})
        },
        [this.translocoService.translate('DURATION_OF_SESSION', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(session.durationInSeconds, v => this.localeService.formatDurationInSecondsWithLabel(v))
        },
        [this.translocoService.translate('OVERALL_SCORE', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(session.scoreOverall, v => this.localeService.formatScoreValue(v))
        },
        [this.translocoService.translate('MESSAGING_SCORE_LABEL', {}, 'reportingV2')]: { 
          type: 'text', 
          value: safeValue(session.scoreMessageAdherence, v => this.localeService.formatScoreValue(v)) 
        },
        [this.translocoService.translate('METHODOLOGY_SCORE', {}, 'reportingV2')]: { 
          type: 'text', 
          value: safeValue(session.scoreObjectives, v => this.localeService.formatScoreValue(v)) 
        },
        [this.translocoService.translate('SKILLS_SCORE', {}, 'reportingV2')]: { 
          type: 'text', 
          value: safeValue(session.scoreSkills, v => this.localeService.formatScoreValue(v)) 
        }
      };
      this.idToStatsMap.set(session.sessionUuid, session);
      return row as TableRow;
    });
  }

  onPageChanged(event: any): void {
    this.currentPage = event.page;
    this.updateScatterPlot();
  }

  onSortChanged(event: any): void {
    if (event && event.direction) {
      this.currentSortDirection = event.direction;
    }
    this.updateScatterPlot();
  }

  private getVisibleSessions(): IUserSessionResponse[] {
    if (!this.tableComponent || !this.tableData || this.tableData.length === 0) {
      return [];
    }

    const visibleSessions = this.paginatedData
      .map(row => {
        const session = this.idToStatsMap.get(row.id);
        return session;
      })
      .filter((stat): stat is IUserSessionResponse => stat !== undefined);
    return visibleSessions;
  }

  private updatePaginatedData(): void {
    if (this.tableComponent) {
      this.paginatedData = this.tableComponent.paginatedData;
    }
  }


  protected onDownloadClick(): void {
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.USER_REPORT_EXPORT_CSV);
    const fileName = this.csvExportService.getCsvFileName(this.userName, 'session_data');
    const sessionIdHeader = this.translocoService.translate('SESSION_ID', {}, 'reportingV2');

    const csvData = this.originalData.map(row => {
      const csvRow: Record<string, unknown> = {
        [sessionIdHeader]: row.id
      };

      this.headers.forEach(header => {
        csvRow[header.name] = row[header.name];
      });

      return csvRow;
    });

    const headerNames = [sessionIdHeader, ...this.headers.map(h => h.name)];

    this.csvExportService.exportToCsv(csvData, fileName, headerNames);
  }

  private initializeFilters() {
    if (!this.originalData?.length) return;

    const uniqueAssignments = [
      ...new Set(
        this.originalData
          .map((row) => row[this.headers[0].name])
          .filter((cell) => cell.type === 'text')
          .map((cell) => (cell as any).value)
      ),
    ];
    this.assignmentOptions = uniqueAssignments.map((assignment) => ({
      label: assignment,
      value: assignment,
    }));

    const uniqueSimulations = [
      ...new Set(
        this.originalData
          .map((row) => row[this.headers[1].name])
          .filter((cell) => cell.type === 'text')
          .map((cell) => (cell as any).value)
      ),
    ];
    this.simulationOptions = uniqueSimulations.map((simulation) => ({
      label: simulation,
      value: simulation,
    }));

    if (this.currentTab === 'practice') {
      this.isSingleSimulationPracticeAssignment = uniqueAssignments.length === 1 && uniqueSimulations.length === 1;
    } else {
      this.isSingleSimulationPracticeAssignment = false;
    }

    const assignmentUuid = this.getAssignmentUuidFromQueryParams();
    if (assignmentUuid && uniqueAssignments.length === 1) {
      this.selectedAssignments = [uniqueAssignments[0]];
    }

    const dateCells = this.userSessions
      .map(s => new Date(s.scoredAt))
      .filter(d => !isNaN(d.getTime()));

    if (dateCells.length) {
      const minDate = new Date(Math.min(...dateCells.map(d => d.getTime())));
      const maxDate = new Date(Math.max(...dateCells.map(d => d.getTime())));

      this.minStartDate = minDate.toISOString().split('T')[0];
      this.maxStartDate = maxDate.toISOString().split('T')[0];
      this.minEndDate = minDate.toISOString().split('T')[0];
      this.maxEndDate = maxDate.toISOString().split('T')[0];
    }
  }

  private updateDropdownOptions(): void {
    const selectedSims = Array.isArray(this.selectedSimulations)
      ? this.selectedSimulations
      : this.selectedSimulations ? [this.selectedSimulations] : [];

    const selectedAssignments = Array.isArray(this.selectedAssignments)
      ? this.selectedAssignments
      : this.selectedAssignments ? [this.selectedAssignments] : [];

    const filteredAssignments = selectedSims.length
      ? new Set(selectedSims.flatMap(sim => this.simulationAssignmentMap[sim] || []))
      : new Set(Object.keys(this.assignmentSimulationMap));

    this.assignmentOptions = Array.from(filteredAssignments).map(a => ({ label: a, value: a }));

    const filteredSimulations = selectedAssignments.length
      ? new Set(selectedAssignments.flatMap(assignment => this.assignmentSimulationMap[assignment] || []))
      : new Set(Object.values(this.assignmentSimulationMap).flat());

    this.simulationOptions = Array.from(filteredSimulations).map(s => ({ label: s, value: s }));
  }

  onDateChange(controlName: 'startDate' | 'endDate', value: string | null): void {
    const dateValue = value ?? '';

    if (controlName === 'startDate') {
      this.startDate = dateValue;
      this.handleStartDateChange(dateValue);
    }

    if (controlName === 'endDate') {
      this.endDate = dateValue;
      this.validateDateRange();
    }

    this.applyFilters();
  }

  private clearFilters(): void {
    this.selectedAssignments = [];
    this.selectedSimulations = [];
    this.assignmentOptions = [];
    this.simulationOptions = [];
    this.startDate = null;
    this.endDate = null;
    this.minStartDate = '';
    this.maxStartDate = '';
    this.minEndDate = '';
    this.maxEndDate = '';
    this.isSingleSimulationPracticeAssignment = false;
    this.isTableSinglePracticeAssignmentAndSimulation = false;
    this.assignmentSimulationMap = {};
    this.simulationAssignmentMap = {};
  }

  private handleStartDateChange(startDateValue: string): void {
    if (startDateValue) {
      this.minEndDate = startDateValue;

      if (this.endDate && new Date(this.endDate) < new Date(startDateValue)) {
        this.endDate = '';
      }
    } else {
      this.minEndDate = this.minStartDate;
    }
  }

  private validateDateRange(): void {
    if (this.startDate && this.endDate) {
      const start = new Date(this.startDate);
      const end = new Date(this.endDate);

      if (end < start) {
        console.warn('Invalid date range: End date is before start date');
        this.endDate = '';
      }
    }
  }

  private applyFilters() {
    let filteredData = [...this.originalData];

    if (this.selectedAssignments.length > 0) {
      filteredData = filteredData.filter((row) => {
        const cell = row[this.headers[0].name];
        if (cell?.type === 'text') {
          return this.selectedAssignments.includes((cell as any).value);
        }
        return false;
      });
    }

    if (this.selectedSimulations.length > 0) {
      filteredData = filteredData.filter((row) => {
        const cell = row[this.headers[1].name];
        if (cell?.type === 'text') {
          return this.selectedSimulations.includes((cell as any).value);
        }
        return false;
      });
    }

    if (this.startDate || this.endDate) {
      filteredData = filteredData.filter((row) => {
        const cell = row[this.headers[2].name];
        // The raw timestamp (sortValue), not the localized display text, which may not parse (e.g. zh-TW "2026/10/27 下午3:30").
        const rawDate = (cell as any)?.sortValue ?? (cell as any)?.value;
        const rowDate = new Date(rawDate);

        if (isNaN(rowDate.getTime())) return false;

        const rowOnly = new Date(rowDate.getFullYear(), rowDate.getMonth(), rowDate.getDate());
        const start = this.startDate ? new Date(this.startDate + 'T00:00:00') : null;
        const end = this.endDate ? new Date(this.endDate + 'T23:59:59') : null;

        return (
          (!start || rowOnly >= start) &&
          (!end || rowOnly <= end)
        );
      });
    }

    this.tableData = filteredData;
    this.currentPage = 1;

    if (!this.isSingleSimulationPracticeAssignment) {
      this.updateDropdownOptions();

      if (this.currentTab === 'practice') {
        this.isTableSinglePracticeAssignmentAndSimulation = this.isTableSingleAssignmentSingleSimulation(this.tableData);
      } else {
        this.isTableSinglePracticeAssignmentAndSimulation = false;
      }
    }
    this.updateScatterPlot();
  }

  private isTableSingleAssignmentSingleSimulation(tableData: TableRow[]): boolean {
    const assignmentColumn = this.headers[0].name;
    const simulationColumn = this.headers[1].name;

    const uniqueAssignments = new Set(
      tableData
        .map(row => (row[assignmentColumn] as any)?.value)
        .filter(v => v != null)
    );

    const uniqueSimulations = new Set(
      tableData
        .map(row => (row[simulationColumn] as any)?.value)
        .filter(v => v != null)
    );

    return uniqueAssignments.size === 1 && uniqueSimulations.size === 1;
  }

  onAssignmentFilterChange(selectedValue: string | string[]) {
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.USER_REPORT_ASSIGNMENT_FILTER, {
      assignment_name: selectedValue || 'Unknown',
    });
    this.selectedAssignments = Array.isArray(selectedValue)
      ? selectedValue
      : [selectedValue];
    this.applyFilters();
  }

  onSimulationFilterChange(selectedValue: string | string[]) {
    this.selectedSimulations = Array.isArray(selectedValue)
      ? selectedValue
      : [selectedValue];
    this.applyFilters();
  }

  onTableRowClick(event: { rowId: string | number; rowData: TableRow }): void {
    const session = this.idToStatsMap.get(event.rowId);
    
    if (session) {
      this.amplitudeService.trackEvent(
        AmplitudeTrackingEvents.USER_REPORT_CLICK_SESSION_SIMULATION_NAME,
        { simulation_name: session.callName }
      );
      
      this.router.navigate(['reporting_dashboard/simulation', session.callUuid], {
        queryParams: {
          moduleAssignmentUuid: session.moduleAssignmentUuid
        },
        state: {
          assignmentName: session.moduleAssignmentName,
          simulationName: session.callName,
          selectedTeamUuids: [],
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.userSessionsSub?.unsubscribe();
  }
}